import { styled } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { HistoryEditor } from 'slate-history';
import { Container, Divider, SxProps } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
    mdiFormatBold,
    mdiFormatItalic,
    mdiFormatUnderline,
    mdiLink,
    mdiFormatHeader1,
    mdiFormatHeader2,
    mdiFormatHeader3,
    mdiFormatHeader4,
    mdiFormatSuperscript,
    mdiFormatListBulleted,
    mdiFormatListNumbered,
    mdiFormatQuoteClose,
    mdiImage,
    mdiFile,
    mdiBorderAll,
    mdiXml,
    mdiFormatClear,
    mdiFormatParagraph,
    mdiRedo,
    mdiUndo,
} from '@mdi/js';

import { Sticky } from 'common/components/Sticky';
import { Icon } from 'common/components/Icon';
import { options } from 'common/components/ContentEditor/options';
import { useContentEditor } from 'common/components/ContentEditor/hooks/useContentEditor';
import { ToolbarFile } from 'common/components/ContentEditor/elements/file';
import { ToolbarImage } from 'common/components/ContentEditor/elements/image';
import { ToolbarEmbed } from 'common/components/ContentEditor/elements/embed/ToolbarEmbed';
import { ToolbarParagraph } from 'common/components/ContentEditor/elements/paragraph';
import { ToolbarTable } from 'common/components/ContentEditor/elements/table/ToolbarTable';

import { ToolbarElement } from './ToolbarElement';
import { ToolbarClearFormat } from './ToolbarClearFormat';
import { ToolbarMark, ToolbarSuperscript } from './ToolbarMark';
import { ToolbarLink } from './ToolbarLink';
import { ToolbarList } from './ToolbarList';
import { IToolbarButtonProps, ToolbarButton } from './ToolbarButton';

export interface IEditorToolbarProps {
    className?: string;
    /** Offset the toolbar sticky position. Defaults to the app bar height. */
    stickyOffset?: number;
}

const stickyInnerNotStickyStyle: SxProps = {
    '.MuiContainer-root': {
        px: 0,
    },
};

const stickyInnerStickyStyle: SxProps = {
    backgroundColor: 'common.white',
    left: 0,
    right: 0,
    minWidth: '100%',
    borderBottom: 1,
    borderBottomColor: 'grey.300',
    '.MuiContainer-root': {
        px: 3,
    },
};

export const BaseEditorToolbar = ({
    className,
    stickyOffset,
}: IEditorToolbarProps) => {
    const { t: translate } = useTranslation();
    const theme = useTheme();
    const editor = useContentEditor();

    const buttonProps: Partial<IToolbarButtonProps> = {};

    if (!editor.selection) {
        buttonProps.active = false;
    }

    return (
        <Sticky
            innerSxNotSticky={stickyInnerNotStickyStyle}
            innerSxSticky={stickyInnerStickyStyle}
            offset={stickyOffset ?? (theme.mixins.toolbar.height as number)}
            outerSx={{ mb: 2 }}
            zIndex={1}
        >
            <Container className={className} maxWidth="md">
                <div className="slate-HeadingToolbar">
                    <ToolbarMark
                        icon={<Icon path={mdiFormatBold} />}
                        tooltip={{ content: translate('editor.bold') }}
                        type={options.bold.type}
                        {...buttonProps}
                    />
                    <ToolbarMark
                        icon={<Icon path={mdiFormatItalic} />}
                        tooltip={{ content: translate('editor.italic') }}
                        type={options.italic.type}
                        {...buttonProps}
                    />
                    <ToolbarMark
                        icon={<Icon path={mdiFormatUnderline} />}
                        tooltip={{ content: translate('editor.underline') }}
                        type={options.underline.type}
                        {...buttonProps}
                    />
                    <ToolbarSuperscript
                        icon={<Icon path={mdiFormatSuperscript} />}
                        tooltip={{ content: translate('editor.superscript') }}
                        {...buttonProps}
                    />

                    <ToolbarLink
                        icon={<Icon path={mdiLink} />}
                        tooltip={{ content: translate('editor.link') }}
                        {...buttonProps}
                    />

                    <Divider flexItem orientation="vertical" />

                    <ToolbarParagraph
                        icon={<Icon path={mdiFormatParagraph} />}
                        tooltip={{ content: translate('editor.paragraph') }}
                        {...buttonProps}
                    />

                    <ToolbarElement
                        icon={<Icon path={mdiFormatHeader1} />}
                        tooltip={{ content: translate('editor.header1') }}
                        type={options.h1.type}
                        {...buttonProps}
                    />
                    <ToolbarElement
                        icon={<Icon path={mdiFormatHeader2} />}
                        tooltip={{ content: translate('editor.header2') }}
                        type={options.h2.type}
                        {...buttonProps}
                    />
                    <ToolbarElement
                        icon={<Icon path={mdiFormatHeader3} />}
                        tooltip={{ content: translate('editor.header3') }}
                        type={options.h3.type}
                        {...buttonProps}
                    />
                    <ToolbarElement
                        icon={<Icon path={mdiFormatHeader4} />}
                        tooltip={{ content: translate('editor.header4') }}
                        type={options.h4.type}
                        {...buttonProps}
                    />

                    <Divider flexItem orientation="vertical" />

                    <ToolbarTable
                        icon={<Icon path={mdiBorderAll} />}
                        tooltip={{ content: translate('editor.table') }}
                        {...buttonProps}
                    />

                    <ToolbarList
                        icon={<Icon path={mdiFormatListBulleted} />}
                        tooltip={{ content: translate('editor.ul') }}
                        typeList={options.ul.type}
                        {...buttonProps}
                    />
                    <ToolbarList
                        icon={<Icon path={mdiFormatListNumbered} />}
                        tooltip={{ content: translate('editor.ol') }}
                        typeList={options.ol.type}
                        {...buttonProps}
                    />
                    <ToolbarElement
                        icon={<Icon path={mdiFormatQuoteClose} />}
                        tooltip={{ content: translate('editor.blockquote') }}
                        type={options.blockquote.type}
                        {...buttonProps}
                    />

                    <Divider flexItem orientation="vertical" />

                    <ToolbarImage
                        icon={<Icon path={mdiImage} />}
                        img={options.img}
                        tooltip={{ content: translate('editor.image') }}
                        {...buttonProps}
                    />
                    <ToolbarFile
                        file={options.file}
                        icon={<Icon path={mdiFile} />}
                        tooltip={{ content: translate('editor.file') }}
                        {...buttonProps}
                    />
                    <ToolbarEmbed
                        icon={<Icon path={mdiXml} />}
                        media_embed={options.media_embed}
                        tooltip={{ content: translate('editor.embed') }}
                        {...buttonProps}
                    />

                    <Divider flexItem orientation="vertical" />

                    <ToolbarClearFormat
                        icon={<Icon path={mdiFormatClear} />}
                        tooltip={{ content: translate('editor.clearFormat') }}
                    />

                    <ToolbarButton
                        icon={<Icon path={mdiUndo} />}
                        tooltip={{ content: translate('editor.undo') }}
                        onMouseDown={(event) => {
                            event.preventDefault();

                            if (HistoryEditor.isHistoryEditor(editor)) {
                                editor.undo();
                            }
                        }}
                        {...buttonProps}
                    />

                    <ToolbarButton
                        icon={<Icon path={mdiRedo} />}
                        tooltip={{ content: translate('editor.redo') }}
                        onMouseDown={(event) => {
                            event.preventDefault();

                            if (HistoryEditor.isHistoryEditor(editor)) {
                                editor.redo();
                            }
                        }}
                        {...buttonProps}
                    />
                </div>
            </Container>
        </Sticky>
    );
};

export const EditorToolbar = styled(BaseEditorToolbar)`
    display: flex;
    margin: auto;

    .slate-HeadingToolbar {
        border-bottom: 0;
        padding: ${({ theme }) => theme.spacing(1, 0)};
        margin: 0;
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        justify-content: center;

        > *:not(:last-child) {
            margin-right: 6px;
        }

        > span {
            display: inline-flex;
            align-items: center;
        }

        > span:hover {
            background-color: ${({ theme }) => theme.vars.palette.grey[100]};
        }
    }

    .slate-ToolbarButton {
        box-sizing: content-box;
        padding: 5px;
        border-radius: 4px;
        height: 28px;
        width: 28px;
        color: ${({ theme }) => theme.vars.palette.text.primary};

        &:hover {
            color: inherit;
        }

        &.slate-ToolbarButton-active {
            color: ${({ theme }) => theme.vars.palette.primary.main};
            background-color: ${({ theme }) => theme.vars.palette.grey[200]};
        }

        > svg {
            width: 24px;
            height: 24px;
        }
    }
`;
